import { useRef, useEffect, useState, useCallback } from "react";
import { log } from "../debug";
import { mapbp, style, isInBounds } from "../properties";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import "../styles/index.scss";
import "../styles/layouts/map.scss";
import "../styles/layouts/routelist.scss";
import UseLocation from "../hooks/UseLocation";
import FloatingWarning from "./FloatingWarning";
import UseMediaParams from "../hooks/UseMediaParams";
import { circlePickSpaces } from "./utils/SpacePickUtils";

mapboxgl.accessToken = mapbp.api_key;

function Map({
  mapFilters,
  pickedSpace,
  setRouteMode,
  routeMode,
  route,
  setPickedById,
  setMapClicked,
  setIsSpacePicked,
  spaces,
  hidden,
  mapTitleClicked,
}) {

  const routeCheckbox = useRef(null);

  //Map
  const mapContainer = useRef(null);
  const map = useRef(null);

  const removeMarkers = useCallback(() => {
    if (markerList.current) {
      Array.prototype.forEach.call(markerList.current, (marker) =>
        marker.remove()
      );
    }
  }, []);

  //list
  const markerList = useRef(null);
  const setMarkers = useCallback(
    (spaces) => {
      removeMarkers();

      markerList.current = spaces.map((space, idx) => {
        const parent = document.createElement("div");
        parent.className = "route-pointer-map";
        const child = document.createElement("div");
        child.innerText = idx + 1;
        parent.replaceChildren(child);

        return new mapboxgl.Marker(parent)
          .setLngLat([...space.coordinates].reverse())
          .addTo(map.current);
      });
    },
    [removeMarkers]
  );

  //Geolocation
  const [locationFixed, setLocationFixed] = useState(false);
  const { coordinates } = UseLocation();
  const locationMarker = useRef(null);

  const setLocationMarker = useCallback(() => {
    const markerHtml = document.createElement("div");
    markerHtml.className = "location-pointer";

    locationMarker.current = new mapboxgl.Marker(markerHtml)
      .setLngLat(coordinates)
      .addTo(map.current);
  }, [coordinates]);

  useEffect(() => {
    if (!coordinates || !map.current) return;

    if (!locationMarker.current) {
      setLocationMarker();
    } else {
      locationMarker.current.setLngLat(coordinates);
      // locationMarker.current.setRotation(heading);
    }
  }, [coordinates, setLocationMarker]);

  const [outOfBoundWarningTrigger, setOutOfBoundWarningTrigger] = useState();
  const [locationWarningTrigger, setLocationWarningTrigger] = useState();
  const flyToMyLocation = useCallback(() => {
    if (map.current && coordinates) {
      if (isInBounds(coordinates)) {
        const maxZoom = 12;
        const currentZoom = map.current.getZoom();
        const zoom = currentZoom > maxZoom ? currentZoom : maxZoom;

        setTimeout(() => {
          setLocationFixed(true);
          map.current.flyTo({
            center: coordinates,
            essential: true,
            duration: 1000,
            zoom: zoom || maxZoom,
          });
        }, 100);
      } else {
        setOutOfBoundWarningTrigger(Date.now());
      }
    } else if (!coordinates) {
      setLocationWarningTrigger(Date.now());
    }
  }, [coordinates]);

  useEffect(() => {
    if (!coordinates) {
      if (locationMarker.current) {
        locationMarker.current.remove();
        locationMarker.current = null;
      }
    } else if (coordinates) {
      if (!locationMarker.current) {
        setLocationMarker();
      }
    }
  }, [coordinates, setLocationMarker]);

  //States
  const width = useRef(window.innerWidth);
  const [clickedFeature, setClickedFeature] = useState(false);
  const [mapStyle, setMapStyle] = useState(false);

  const [loaded, setLoaded] = useState(false);
  // const styleLoaded = useRef(null);
  const [styleLoaded, setStyleLoaded] = useState(false);

  // useEffect(() => {
  //   setMarkers(route);
  // }, [route, setMarkers]);

  useEffect(() => {
    if (routeModeRef.current) {
      setMarkers(route);
    }
  }, [route, setMarkers]);

  const removeClickedFeatureOnMap = useCallback(() => {
    try {
      map.current.setLayoutProperty(
        "spaces-layer",
        "icon-image",
        style.whiteIcon.name
      );
    } catch (e) {
      log(e);
    }
  }, []);

  useEffect(() => {
    if (styleLoaded) {
      setFeatureClicked();
    }
  }, [styleLoaded])

  const setFeatureClicked = useCallback(() => {
    if (!pickedSpace) return;
    const iconimg = [
      "match",
      ["get", "id"],
      pickedSpace.id,
      style.whiteClickedIcon.name,
      style.whiteIcon.name,
    ];

    if (!map.current.getLayer("spaces-layer")) return;

    map.current.setLayoutProperty("spaces-layer", "icon-image", iconimg);
  }, [pickedSpace])

  const applyPickedSpace = useCallback(() => {
    if (!pickedSpace) {
      setIsSpacePicked(false);
      removeClickedFeatureOnMap();
      return;
    }

    setFeatureClicked();

    setIsSpacePicked(true);
    const maxZoom = 16;
    const currentZoom = map.current.getZoom();
    const lng = pickedSpace.coordinates[1];
    const lat = pickedSpace.coordinates[0];
    const zoom = currentZoom > maxZoom ? currentZoom : maxZoom;

    map.current.flyTo({
      center: [lng, lat],
      essential: true,
      duration: 2000,
      zoom: zoom || maxZoom,
    });
  }, [pickedSpace, removeClickedFeatureOnMap, setIsSpacePicked])

  useEffect(() => {
    applyPickedSpace();
  }, [pickedSpace]);

  const actPickFeature = useCallback(
    (feature) => {
      // setClickedFeatureOnMap(feature);
      //TODO SET PICKED SPACE ON ITS OWN
      //without roundtrip to mapcontainer? Consider...
      setPickedById(feature.id);
      setIsSpacePicked(true);
    },
    [setIsSpacePicked, setPickedById]
  );

  const actClearFeatures = useCallback(() => {
    removeClickedFeatureOnMap();
    setClickedFeature(undefined);
    setIsSpacePicked(false);
    //used to force SearchBar to update when map is clicked
    //using date.now because we cannot use a counter which
    //would cause useEffect infinite loops (dependencies)
    setMapClicked(Date.now());
  }, [removeClickedFeatureOnMap, setIsSpacePicked, setMapClicked]);

  const onClickMapHandleFeature = useCallback(
    (e) => {
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ["spaces-layer"],
      });

      if (features && features.length > 0) {
        const next = circlePickSpaces(features);
        const chosen = features[next];

        setPickedById(chosen.id);
      } else {
        setMapClicked(Date.now());
      }
    },
    [setMapClicked, setPickedById]
  );
  // these dimensions are based on barHeight, from generic scss
  const mobileOffset = 60;
  const fullOffset = 90;
  const { isMobile, size } = UseMediaParams();
  const [mapHeight, setMapHeight] = useState(
    window.innerHeight - (isMobile ? mobileOffset : fullOffset)
  );

  //Map Resizing
  const handleResizeWindow = useCallback(() => {
    width.current = size.width;
    if (!map.current) return;

    const offset = (isMobile ? mobileOffset : fullOffset);
    const newHeight = size.height - offset;

    setMapHeight(newHeight);
    setTimeout(() => {
      map.current.resize();
    }, 10);
  }, [isMobile, size.height, size.width]);

  useEffect(() => {
    handleResizeWindow();
  }, [size, handleResizeWindow])

  const showPointer = () => {
    const mapboxcanvas = document.getElementsByClassName("mapboxgl-canvas")[0];
    mapboxcanvas.classList.add("pointingAtLayer");
  };

  const hidePointer = () => {
    const mapboxcanvas = document.getElementsByClassName("mapboxgl-canvas")[0];
    mapboxcanvas.classList.remove("pointingAtLayer");
  };

  const routeModeRef = useRef(null);
  useEffect(() => {
    routeModeRef.current = routeMode;
    routeCheckbox.current.checked = routeMode;
    //if switching off route mode, remove the invisible picked feature
    !routeMode && setClickedFeature(undefined);

    setIsSpacePicked(false);
    removeMarkers();
    removeClickedFeatureOnMap();
    //set on clicks

    setMapClicked(Date.now());
  }, [
    removeClickedFeatureOnMap,
    removeMarkers,
    routeMode,
    setIsSpacePicked,
    setMapClicked,
  ]);

  const switchRouteMode = useCallback(
    (e) => {
      // showFeatureList();
      const on = e.target.checked;
      if (styleLoaded) {
        setRouteMode((rtMode) => {
          return on === false || on === true ? on : !rtMode;
        });
      }
    },
    [setRouteMode, styleLoaded]
  );

  /**
   * Reload all map data: images, source and layers
   * "oncedone" will be executed at the end
   *
   * @param {*} oncedone
   */
  const reloadMapData = useCallback(
    (oncedone) => {
      const imagePaths = [
        { id: style.whiteIcon.name, path: style.whiteIcon.path },
        { id: style.whiteClickedIcon.name, path: style.whiteClickedIcon.path },
        { id: style.icon.name, path: style.icon.path },
        { id: style.clickedIcon.name, path: style.clickedIcon.path },
      ];

      const imageLoads = imagePaths.map((imgObj) => {
        return new Promise((resolve, reject) => {
          map.current.loadImage(imgObj.path, (err, img) => {
            if (err) {
              log(err);
              reject(err);
              return;
            }

            map.current.addImage(imgObj.id, img);

            resolve();
          });
        });
      });

      Promise.allSettled(imageLoads).then(() => {
        var mapLayer = map.current.getLayer("spaces-layer");
        if (typeof mapLayer !== "undefined") {
          // Remove map layer & source.
          map.current
            .removeLayer("spaces-layer")
            .removeSource(mapbp.layers.spaces.source);
        }

        map.current.once("render", () => {
          setLoaded(true);
        });

        map.current.addSource(mapbp.layers.spaces.source, {
          data:
            `${process.env.REACT_APP_API_URL}/files` ||
            "https://api.npoint.io/e6ec76b51550241fc33c",
          type: "geojson",
          promoteId: "id",
          // generateId: true
        });

        map.current.addLayer({
          id: "spaces-layer",
          type: "symbol",
          source: mapbp.layers.spaces.source,
          layout: {
            "icon-image": style.whiteIcon.name,
            "icon-size": width.current > 418 ? 0.17 : 0.12,
            "icon-rotate": 0,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "text-allow-overlap": true,
            "text-ignore-placement": true,
            // "icon-opacity": 0.5,
            // "text-field": ["get", "space_name"],
            // "text-size": 15,
            // "text-font": ["Source Code Pro Black"],
            // "text-field": ["get", "point_count"],
          },
          paint: {
            "text-color": "#ffffff",
            "text-translate": [70, 0],
          },
        });

        if (oncedone) oncedone();
      });
    },
    [width]
  );

  /**
   * Switches map mode/style
   * TODO: for now, this implies reloading the entire map content
   * In the future, there should be a better way
   */
  const switchMapStyle = useCallback(() => {
    if (styleLoaded) {
      if (mapStyle) {
        map.current.setStyle(mapbp.bwStyle);
      } else {
        map.current.setStyle(mapbp.style);
      }

      //TODO: need to fix when map is switched:
      //we should keep the picked feature after refresh
      //also there is a jitter and the searchbar is not
      //immediately closed
      setLoaded(false);
      //set picked as false to avoid jitter when switching map mode
      //TODO: should find way to keep picked feature, but his will do for now...
      // removeClickedFeatureOnMap();

      setStyleLoaded(false);
      map.current.once("styledata", () => {
        reloadMapData(() => {
          setLoaded(true);
        });
        genericFlyTo(1000);
      });

      setRouteMode(false);

      setMapStyle(!mapStyle);
    }
  }, [mapStyle, reloadMapData, removeClickedFeatureOnMap, applyPickedSpace, styleLoaded]);

  const showFeatureList = useCallback(() => {
    const featureList = getFeatureList(mapFilters);

    const getDiff = (arr1, arr2) => {
      let a,
        b,
        res = [];
      if (arr1 > arr2) {
        a = arr1;
        b = arr2;
      } else {
        a = arr2;
        b = arr1;
      }

      for (let i = 0; i < a.length; i++) {
        const f = b.filter((u) => {
          return u.id !== a[i].id;
        });
        if (!f || f.length === 0) {
          // log(a[i]);
          res.push(a[i]);
        }
      }

      return res;
    };

    const diff = getDiff(featureList, spaces);

    log(diff);
  }, [mapFilters, spaces]);

  /**
   * Given {@link mapFilters}, returns a filtered, sorted list of unique features
   *
   * @param {*} mapFilters
   * @returns
   */
  const getFeatureList = (mapFilters) => {
    const params = {
      sourceLayer: "spaces-layer",
    };
    if (mapFilters && mapFilters.length > 0) {
      params.filter = mapFilters;
    }

    const featList = map.current.querySourceFeatures(
      mapbp.layers.spaces.source,
      params
    );

    if (!featList || featList.length <= 0) {
      return [];
    }

    const filteredFeatList = [];
    const names = [];

    featList.forEach((feat) => {
      const n = feat.properties.name;
      if (!names.includes(n)) {
        filteredFeatList.push(feat);
        names.push(n);
      }
    });

    filteredFeatList.sort((feat1, feat2) =>
      feat1.properties.name.localeCompare(feat2.properties.name)
    );

    return filteredFeatList;
  };

  /**
   * Applies filters to the map and sets the new filtered features list
   */
  const applyMapFilters = useCallback(() => {
    if (!map.current) return;
    map.current.setZoom(11);

    if (!mapFilters || mapFilters.length === "undefined") {
      return;
    }

    const applyFilters = () => {
      //TODO: check this page to try and listen to source added/loaded events
      //https://gis.stackexchange.com/questions/240134/mapbox-gl-js-source-loaded-event
      if (loaded) {
        map.current.setFilter("spaces-layer", null);
        if (mapFilters && mapFilters.length > 0) {
          map.current.setFilter("spaces-layer", mapFilters);
        }

        removeClickedFeatureOnMap();
        //remove any picked features
        // eslint-disable-next-line no-undef
        setIsSpacePicked(false);
      }
    };

    if (!styleLoaded) {
      //guarantee that apply filters is only called
      // when everything is properly loaded
      map.current.once("sourcedata", () => {
        const intId = setInterval(() => {
          if (
            map.current.getSource(mapbp.layers.spaces.source) &&
            map.current.isSourceLoaded(mapbp.layers.spaces.source)
          ) {
            applyFilters();
            setStyleLoaded(true);
            clearTimeout(intId);
            // showFeatureList();
          }
        }, 400);
      });
    } else {
      applyFilters();
      // showFeatureList();
    }
  }, [styleLoaded, loaded, mapFilters, removeClickedFeatureOnMap, setIsSpacePicked]);

  /**
   * Map Title Click UE
   */
  const genericFlyTo = useCallback((delay) => {
    setTimeout(() => {
      const center = [mapbp.startingPosition.lng, mapbp.startingPosition.lat];
      map.current.flyTo({
        center: center,
        essential: true,
        duration: 4000,
        zoom: 13,
      });
    }, delay || 100);
  }, []);

  useEffect(() => {
    if (!map.current) return;

    setClickedFeature(undefined);
    setRouteMode(false);

    genericFlyTo();

    //not the prettiest solution, but mapbox was freaking out for some reason
  }, [genericFlyTo, mapTitleClicked, setRouteMode]);


  const [firstTime, setFirstTime] = useState(true);
  useEffect(() => {
    setFirstTime(false);
  }, []);

  /**
   * Main UE
   */
  useEffect(
    () => {
      const buildMap = (style) => {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: style ? style : mapbp.bwStyle,
          center: [mapbp.startingPosition.lng, mapbp.startingPosition.lat],
          zoom: mapbp.startingPosition.zoom,
          maxBounds: mapbp.bounds,
          touchZoomRotate: false,
          preserveDrawingBuffer: true,
        });

        map.current.setMaxPitch(0);

        map.current.touchZoomRotate.enable();
        map.current.dragRotate.disable();
        map.current.touchZoomRotate.disableRotation();

        map.current.on("load", () => {
          map.current.resize();
        });

        map.current.once("load", () => {
          reloadMapData(() => {
            if (firstTime) genericFlyTo(200);
          });
        });

        map.current.on("mouseenter", "spaces-layer", showPointer);
        map.current.on("mouseleave", "spaces-layer", hidePointer);

        map.current.on("click", onClickMapHandleFeature);
        map.current.on("touchmove", () => { setMapClicked(Date.now()) });

        //remove location centered icon
        ["touchmove", "drag"].forEach((event) => {
          map.current.on(event, () => {
            setLocationFixed(false);
          });
        });
      };
      if (!map.current) {
        buildMap();
      }
    },
    [firstTime, genericFlyTo, handleResizeWindow, onClickMapHandleFeature, reloadMapData, setMapClicked],
    ["lng", "lat", "zoom", "setClickedFeatureOnMap"],
    "main"
  );

  /**
   * Loaded UE
   */
  useEffect(() => {
    loaded && applyMapFilters();
  }, [loaded]);

  /**
   * Map Filters UE
   */
  useEffect(
    () => {
      //revert to normal mode
      setRouteMode(false);
      applyMapFilters();
    },
    [mapFilters, setRouteMode],
    ["mapFilters", "applyMapFilters"],
    "apply filters, set clicked"
  );

  /**
   * Resize UE
   */
  useEffect(() => {
    handleResizeWindow();
    setMapClicked(Date.now());
  }, [hidden]);

  return (
    <div
      style={{
        position: "relative",
        visibility: `${hidden ? "hidden" : "visible"}`,
      }}
    >
      <FloatingWarning
        trigger={outOfBoundWarningTrigger}
        getMessage={() => {
          return <div>Localização disponível no concelho do Porto</div>;
        }}
      />
      <FloatingWarning
        getMessage={() => {
          return `Para continuar, ative a localização do seu dispositivo.`;
        }}
        getExtendedMessage={() => {
          return (
            <div>
              <br />
              <div>No dispositivo, terá que:</div>
              <ul>
                <li>Ativar a localização e o Wi-Fi ou redes móveis.</li>
                <li>
                  Conceder permissões de acesso à localização no browser
                  predefinido.
                </li>
              </ul>
            </div>
          );
        }}
        trigger={locationWarningTrigger}
      />
      <div className="bar-container"></div>
      <div style={{ overflow: 'hidden' }}>
        <div style={{ height: mapHeight }} ref={mapContainer} />
      </div>
      <div className="map-buttons-container">
        <div className="map-button">
          <input
            type="checkbox"
            onClick={flyToMyLocation}
            className={
              locationFixed ? `get-location-button-hit` : `get-location-button`
            }
          ></input>
        </div>
        <div className="map-button">
          <input
            ref={routeCheckbox}
            onChange={switchRouteMode}
            className="route-button"
            type="checkbox"
          ></input>
        </div>
        <div className="map-button">
          <input
            onChange={switchMapStyle}
            checked={!!mapStyle}
            className="style-button"
            type="checkbox"
          ></input>
        </div>
      </div>
    </div >
  );
}

export default Map;
